import { CheckIcon, MinusIcon } from "@phosphor-icons/react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithRef } from "react";

// @Utils
import { cn } from "@/lib/utils";

// @Type
export interface CheckboxProps
  extends ComponentPropsWithRef<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {
  isSelectAll?: boolean;
  isPartialSelect?: boolean;
  iconClassNames?: string;
  size?: "sm" | "default";
}

const checkboxVariants = cva(
  "peer bg-white h-4 w-4 shrink-0 ring-offset-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-750 focus-visible:ring-offset-2 disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "rounded-sm border border-slate-200 hover:bg-white hover:border-violet-800 data-[state=checked]:bg-violet-800 data-[state=checked]:border-none disabled:border-grey-200",
        secondary:
          "rounded-xs border border-slate-200 dark:border-slate-800 hover:bg-white hover:border-gray-600 disabled:border-grey-200 text-slate-800",
      },
    },
    defaultVariants: {
      variant: "primary",
    },
  },
);

const Checkbox = ({
  className,
  iconClassNames,
  isSelectAll = false,
  isPartialSelect = false,
  variant = "primary",
  size = "default",
  ref,
  ...props
}: CheckboxProps) => {
  const iconMap = {
    selectAllChecked: (
      <CheckIcon
        className={cn(
          "h-[11px] w-[11px] text-white",
          variant === "secondary" && props.checked && "text-slate-800",
        )}
      />
    ),
    selectAllPartial: (
      <MinusIcon
        className={cn("h-[11px] w-[11px] text-slate-800", iconClassNames)}
      />
    ),

    defaultChecked: (
      <CheckIcon
        className={cn(
          "h-[11px] w-[11px]",
          iconClassNames,
          variant === "secondary" && props.checked
            ? "text-slate-800"
            : "text-white",
        )}
      />
    ),
  };

  const renderIcon = () => {
    if (isSelectAll)
      return isPartialSelect
        ? iconMap.selectAllPartial
        : iconMap.selectAllChecked;
    return iconMap.defaultChecked;
  };

  return (
    <div className="flex items-start space-x-2 min-w-0">
      <CheckboxPrimitive.Root
        ref={ref}
        data-slot="checkbox"
        className={cn(checkboxVariants({ variant }), className)}
        {...props}
      >
        <CheckboxPrimitive.Indicator
          forceMount
          className={cn("flex items-center justify-center text-current")}
        >
          {renderIcon()}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {props.children && (
        <label
          htmlFor={props.id}
          className={cn(
            "pl-0.5 text-gray-700 leading-none cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70 wrap-break-word min-w-0 flex-1",
            size === "sm" ? "text-xs" : "text-sm",
          )}
        >
          {props.children}
        </label>
      )}
    </div>
  );
};
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
export default Checkbox;
